import { useState, useRef, useCallback, useEffect } from 'react';
import { transcribeAudioForMath } from '../../../../config/api';
import { parseSpokenNumber } from '../../../../utils/parseSpokenNumber';
import { progressQueue } from '../../../../services/ProgressQueueService';
import { logError } from '../../../../utils/errorReporting';

interface VoiceResult {
  questionId: string;
  isCorrect: boolean;
}

const FINALIZE_TIMEOUT_MS = 30000;
const CAPTURE_BUFFER_MS = 300; // Extra recording time after space press to capture trailing speech

function getSupportedMimeType(): string | undefined {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) {
      return mime;
    }
  }
  return undefined;
}

export function useVoiceAssessment(
  enabled: boolean,
  userId: string,
  assessmentId: string
) {
  const [isRecording, setIsRecording] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);

  const mountedRef = useRef(true);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeTypeRef = useRef<string | undefined>(undefined);
  const resultsRef = useRef<VoiceResult[]>([]);
  const transcriptionPromisesRef = useRef<Promise<void>[]>([]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const initializeMicrophone = useCallback(async (): Promise<boolean> => {
    if (!enabled) return false;

    if (!navigator.mediaDevices?.getUserMedia) {
      if (mountedRef.current) {
        setMicError('Microphone not supported in this browser.');
      }
      return false;
    }

    const mimeType = getSupportedMimeType();
    if (!mimeType) {
      if (mountedRef.current) {
        setMicError('Audio recording is not supported in this browser.');
      }
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      if (!mountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return false;
      }

      streamRef.current = stream;
      mimeTypeRef.current = mimeType;

      // Start an initial recorder so we're ready for the first question
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      recorder.start();
      mediaRecorderRef.current = recorder;

      if (mountedRef.current) {
        setIsRecording(true);
        setMicError(null);
      }
      return true;
    } catch {
      if (mountedRef.current) {
        setMicError(
          'Microphone permission denied. Please allow microphone access and try again.'
        );
      }
      return false;
    }
  }, [enabled]);

  const captureAndAdvance = useCallback(
    (questionId: string, expectedAnswer: number, questionStartTime: number): boolean => {
      const recorder = mediaRecorderRef.current;
      const stream = streamRef.current;
      const mimeType = mimeTypeRef.current;

      if (!recorder || recorder.state === 'inactive' || !stream || !mimeType) {
        return false;
      }

      // Grab existing chunks from the old recorder
      const oldChunks: Blob[] = [...chunksRef.current];
      chunksRef.current = [];

      // Start new recorder IMMEDIATELY on the same stream for the next question
      // (both recorders run in parallel for CAPTURE_BUFFER_MS)
      const newRecorder = new MediaRecorder(stream, { mimeType });
      newRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      newRecorder.start();
      mediaRecorderRef.current = newRecorder;

      // Let old recorder run CAPTURE_BUFFER_MS longer to capture trailing speech,
      // then stop it and create the blob
      const blobPromise = new Promise<Blob>((resolve) => {
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) oldChunks.push(e.data);
        };
        recorder.onstop = () => {
          resolve(new Blob(oldChunks, { type: mimeType }));
        };

        setTimeout(() => {
          if (recorder.state !== 'inactive') {
            recorder.stop();
          }
        }, CAPTURE_BUFFER_MS);
      });

      const timeSpent = Date.now() - questionStartTime;

      if (mountedRef.current) {
        setPendingCount((c) => c + 1);
      }

      const transcriptionPromise = blobPromise
        .then((blob) => transcribeAudioForMath(blob))
        .then((transcript) => {
          const parsed = parseSpokenNumber(transcript);
          const isCorrect = parsed !== null && parsed === expectedAnswer;

          if (mountedRef.current) {
            resultsRef.current.push({ questionId, isCorrect });
          }

          if (userId) {
            try {
              const factId = questionId.split('FACT#')[1];

              progressQueue.enqueueProgressAssessmentUpdate(userId, assessmentId, {
                [questionId]: {
                  attempts: 1,
                  correct: isCorrect ? 1 : 0,
                  timeSpent,
                },
              });

              if (factId) {
                progressQueue.enqueueProgressUpdate(userId, {
                  facts: {
                    [factId]: {
                      attempts: 1,
                      correct: isCorrect ? 1 : 0,
                      timeSpent,
                      practiceContext: 'progressAssessment',
                    },
                  },
                });
              }
            } catch (err) {
              logError(err, {
                component: 'useVoiceAssessment-captureAndAdvance',
                userId,
                questionId,
                assessmentId,
              });
            }
          }
        })
        .catch((err) => {
          logError(err, {
            component: 'useVoiceAssessment-transcription',
            userId,
            questionId,
          });
          // Count as attempted but incorrect on transcription failure
          if (mountedRef.current) {
            resultsRef.current.push({ questionId, isCorrect: false });
          }
        })
        .finally(() => {
          if (mountedRef.current) {
            setPendingCount((c) => Math.max(0, c - 1));
          }
        });

      transcriptionPromisesRef.current.push(transcriptionPromise);

      return true;
    },
    [userId, assessmentId]
  );

  const finalizeResults = useCallback(async (
    currentQuestion?: { id: string; answer: number },
    questionStartTime?: number
  ): Promise<{
    totalAttempted: number;
    totalCorrect: number;
  }> => {
    // Stop the current in-progress recorder and enqueue its transcription
    const recorder = mediaRecorderRef.current;
    const stream = streamRef.current;
    const mimeType = mimeTypeRef.current;

    if (recorder && recorder.state !== 'inactive' && stream && mimeType) {
      const finalBlobPromise = new Promise<Blob>((resolve) => {
        const allChunks: Blob[] = [...chunksRef.current];
        chunksRef.current = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) allChunks.push(e.data);
        };
        recorder.onstop = () => {
          resolve(new Blob(allChunks, { type: mimeType }));
        };
      });

      recorder.stop();
      mediaRecorderRef.current = null;

      if (mountedRef.current) {
        setIsRecording(false);
        setPendingCount((c) => c + 1);
      }

      const finalPromise = finalBlobPromise
        .then((blob) => transcribeAudioForMath(blob))
        .then((transcript) => {
          if (!currentQuestion) return;

          const parsed = parseSpokenNumber(transcript);
          const isCorrect = parsed !== null && parsed === currentQuestion.answer;
          const timeSpent = questionStartTime ? Date.now() - questionStartTime : 0;

          if (mountedRef.current) {
            resultsRef.current.push({ questionId: currentQuestion.id, isCorrect });
          }

          if (userId) {
            try {
              const factId = currentQuestion.id.split('FACT#')[1];

              progressQueue.enqueueProgressAssessmentUpdate(userId, assessmentId, {
                [currentQuestion.id]: {
                  attempts: 1,
                  correct: isCorrect ? 1 : 0,
                  timeSpent,
                },
              });

              if (factId) {
                progressQueue.enqueueProgressUpdate(userId, {
                  facts: {
                    [factId]: {
                      attempts: 1,
                      correct: isCorrect ? 1 : 0,
                      timeSpent,
                      practiceContext: 'progressAssessment',
                    },
                  },
                });
              }
            } catch (err) {
              logError(err, {
                component: 'useVoiceAssessment-finalizeResults-lastClip',
                userId,
                questionId: currentQuestion.id,
                assessmentId,
              });
            }
          }
        })
        .catch((err) => {
          logError(err, {
            component: 'useVoiceAssessment-finalizeResults-lastClip',
            userId,
          });
          // Score as incorrect on failure
          if (currentQuestion && mountedRef.current) {
            resultsRef.current.push({ questionId: currentQuestion.id, isCorrect: false });
          }
        })
        .finally(() => {
          if (mountedRef.current) {
            setPendingCount((c) => Math.max(0, c - 1));
          }
        });

      transcriptionPromisesRef.current.push(finalPromise);
    }

    // Wait for all pending transcriptions, with a 30s safety timeout
    const allSettled = Promise.allSettled(transcriptionPromisesRef.current);
    const timeout = new Promise<void>((resolve) =>
      setTimeout(resolve, FINALIZE_TIMEOUT_MS)
    );
    await Promise.race([allSettled, timeout]);

    const results = resultsRef.current;
    const totalAttempted = results.length;
    const totalCorrect = results.filter((r) => r.isCorrect).length;

    return { totalAttempted, totalCorrect };
  }, [userId, assessmentId]);

  const releaseMicrophone = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    if (mountedRef.current) {
      setIsRecording(false);
    }
  }, []);

  return {
    isRecording,
    micError,
    initializeMicrophone,
    captureAndAdvance,
    finalizeResults,
    releaseMicrophone,
    pendingCount,
  };
}
